// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, from, Observable, of } from "rxjs"
import { Action } from "redux"
import { filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as UserContextSlice from "../../user-context/slice"
import * as Actions from "../actions"
import { RootState } from "../../../app/store"
import { isDisabledTimeGroup } from "../timeGroups"
import { SelectTimesheetFieldOptionRequest, ClearTimesheetFieldRequest } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

export const syncTimesheetFieldSelections = () =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Slice.setTimeGroupFieldConfigs.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => of(action.payload)
        .pipe(
          mergeMap(() => {
            const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
            const timeGroupsCache = Slice.selectTimeGroupsCache(state)
            const timeGroup = timeGroupsCache[action.payload.timeGroupId]
            
            // Only process unposted time groups
            if (!currentSiteId || !timeGroup || isDisabledTimeGroup(timeGroup.postStatus)) {
              return EMPTY
            }
            
            const configuredTimesheetFields = Slice.selectConfiguredTimesheetFieldsCombiner(timeGroup.id, currentSiteId, state.timeline)
            
            // Find required fields that need default suggestions
            const fieldsNeedingSuggestions = configuredTimesheetFields.filter(field =>
              field.timesheetFieldConfig.isRequiredField &&
              field.timesheetFieldConfig.suggestedTimesheetFieldOptionId &&
              !field.selectedTimesheetFieldOption &&
              field.timesheetFieldConfig.optionsList.length > 0
            )
            
            // Find fields where selected option is no longer in the config options
            const fieldsNeedingClearing = configuredTimesheetFields.filter(field =>
              field.selectedTimesheetFieldOption &&
              !field.timesheetFieldConfig.optionsList.some(option =>
                option.id === field.selectedTimesheetFieldOption?.timesheetFieldOptionId
              )
            )
            
            const actions: Action[] = []
            
            // Add clear actions for fields with invalid selections
            const clearActions = fieldsNeedingClearing.map(field =>
              Actions.clearTimesheetField(
                new ClearTimesheetFieldRequest()
                  .setTimeGroupId(timeGroup.id)
                  .setTimesheetFieldId(field.timesheetField.id)
                  .setSiteId(currentSiteId)
              )
            )
            actions.push(...clearActions)
            
            // Add select actions for fields needing suggestions
            const selectActions = fieldsNeedingSuggestions.map(field =>
              Actions.selectTimesheetFieldOption(
                new SelectTimesheetFieldOptionRequest()
                  .setTimeGroupId(timeGroup.id)
                  .setTimesheetFieldId(field.timesheetField.id)
                  .setSiteId(currentSiteId)
                  .setTimesheetFieldOptionId(field.timesheetFieldConfig?.suggestedTimesheetFieldOptionId ?? "")
              )
            )
            actions.push(...selectActions)
            
            if (actions.length === 0) {
              return EMPTY
            }
            
            return from(actions)
          }),
        )),
    )

export default syncTimesheetFieldSelections()
